import { useRef, useEffect } from 'react';

interface UseMagneticHoverOptions {
  strength?: number;
  tolerance?: number;
}

export function useMagneticHover<T extends HTMLElement>({
  strength = 20,
  tolerance = 100,
}: UseMagneticHoverOptions = {}) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let animationFrameId: number | null = null;
    let cachedRect: DOMRect | null = null;
    let lastCacheTime = 0;
    const CACHE_DURATION = 100; // Cache rect for 100ms to reduce reflows

    const updateCachedRect = () => {
      const now = performance.now();
      if (!cachedRect || now - lastCacheTime > CACHE_DURATION) {
        cachedRect = element.getBoundingClientRect();
        lastCacheTime = now;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Cancel any pending animation frame
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }

      // Schedule update in next frame to batch DOM operations
      animationFrameId = requestAnimationFrame(() => {
        updateCachedRect();
        
        if (!cachedRect) return;

        const centerX = cachedRect.left + cachedRect.width / 2;
        const centerY = cachedRect.top + cachedRect.height / 2;
        
        const distanceX = e.clientX - centerX;
        const distanceY = e.clientY - centerY;
        const distance = Math.sqrt(distanceX ** 2 + distanceY ** 2);

        if (distance < tolerance) {
          const moveX = (distanceX / tolerance) * strength;
          const moveY = (distanceY / tolerance) * strength;
          element.style.transform = `translate(${moveX}px, ${moveY}px)`;
        } else {
          element.style.transform = 'translate(0, 0)';
        }

        animationFrameId = null;
      });
    };

    const handleMouseLeave = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      element.style.transform = 'translate(0, 0)';
    };

    const handleResize = () => {
      cachedRect = null; // Invalidate cache on resize
    };

    document.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('resize', handleResize);

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', handleResize);
    };
  }, [strength, tolerance]);

  return ref;
}
